import React from 'react';
import { MetaTags } from '@/components/MetaTags';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { useModalStore } from '@/lib/stores/modal-store';
import {
  ABOUT_SEO,
  ABOUT_SHARED_COPY,
} from '@/content/about-public';

// About — "1c retoned" editorial feature (Design SoT `About Retoned.dc.html`,
// founder pick 2026-07-19). Chaptered correspondence on the invite/whisper
// palette: canvas bg, Cormorant display, tracked-caps chrome, hairline rules.
// Crawler/SEO strings live in content/about-public.ts (shared with prerender).

const CDN_BASE = 'https://rendezvouswebsite.b-cdn.net';
const carousel = (n: number) =>
  `${CDN_BASE}/experience%20carousel/Carousel%20Media%20(${n}).jpg`;

const v = {
  canvas: 'var(--color-invite-canvas)',
  void: 'var(--color-invite-void)',
  bone: 'var(--color-invite-bone)',
  ivory: 'var(--color-invite-ivory)',
  mute: 'var(--color-invite-mute)',
  dim: 'var(--color-invite-dim)',
  brass: 'var(--color-invite-brass)',
  brassMute: 'var(--color-invite-brass-mute)',
  hair: 'var(--color-invite-hair)',
  hairStrong: 'var(--color-invite-hair-strong)',
} as const;

const display = "'Cormorant Garamond', Georgia, serif";

function ChapterMark({ numeral, title }: { numeral: string; title?: string }) {
  return (
    <div className="text-center">
      <div
        style={{ fontFamily: display, color: v.brassMute }}
        className="text-base tracking-[0.3em]"
      >
        {numeral}
      </div>
      <div
        className="mx-auto mt-4 h-10 w-px"
        style={{ backgroundColor: v.hairStrong }}
      />
      {title ? (
        <h2
          style={{ fontFamily: display, color: v.bone }}
          className="mt-6 text-4xl font-light sm:text-5xl"
        >
          {title}
        </h2>
      ) : null}
    </div>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="text-[13px] font-medium uppercase tracking-[0.35em]"
      style={{ color: v.brassMute }}
    >
      {children}
    </div>
  );
}

type Era = {
  year: string;
  title: string;
  body: string;
  aside?: string;
  image: string;
};

const ERAS: Era[] = [
  {
    year: "'23",
    title: 'The Spark',
    body: 'A sun-drenched reunion in Malta. Intimate dinners in New York and Miami. A mischief-filled NYE blowout in Cape Town.',
    aside: 'It was meant to be a few gatherings. Instead, it lit a fire.',
    image: carousel(8),
  },
  {
    year: "'24",
    title: 'The Snowball',
    body: 'Verbier (we only lost one snowboarder). A concert takeover in Ibiza. A beach festival in Turkey that turned strangers into chosen family. Six weeks through India that broke us open.',
    aside: "By then, it wasn't a project anymore. It was a movement.",
    image: carousel(35),
  },
  {
    year: "'25",
    title: 'The Build',
    body: 'We finally chased the digital side for real—an experiences marketplace first, then the first proper build of the platform. Life got loud in the middle of it: a baby on the way for one of us, a move to New York for the other. Both of us lining up new day jobs, both set on keeping rendezvous going through it all.',
    image: carousel(46),
  },
  {
    year: "'26",
    title: 'The Regroup',
    body: 'A newborn at home. An engagement and a visa in the works. A season of getting our own lives squared away—heads down, but never gone.',
    aside: 'The plan is simple: come back strong this fall.',
    image: carousel(20),
  },
];

const BELIEF_DETAILS = [
  "Into spreadsheets but still make time for dance floors? You'll fit in.",
  'Effortless again—even magical sometimes.',
  "Malta to India—people who remind you life's worth showing up for.",
  'Community over clout. Grounded energy. Radically inclusive.',
] as const;

const BELIEFS: Array<{ statement: string; detail: string }> =
  ABOUT_SHARED_COPY.beliefs.map((statement: string, index: number) => ({
    statement,
    detail: BELIEF_DETAILS[index] ?? '',
  }));

export function About() {
  const { openAuthModal } = useModalStore();
  const { user } = useUnifiedAuth();
  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: v.canvas, color: v.bone, fontFamily: "'Inter', sans-serif" }}
      data-testid="about-1c"
    >
      <MetaTags
        title={ABOUT_SEO.title}
        description={ABOUT_SEO.description}
        image={ABOUT_SEO.image}
        url={ABOUT_SEO.url}
      />
      {/* HERO — the quiet question */}
      <section className="px-6 pt-28 sm:pt-36 lg:pt-40">
        <div className="mx-auto max-w-[980px] text-center">
          <Eyebrow>Our story</Eyebrow>
          <h1
            style={{ fontFamily: display, color: v.bone }}
            className="mx-auto mt-8 text-[52px] font-light leading-[1] tracking-[-0.015em] sm:text-[76px] lg:text-[110px]"
          >
            {ABOUT_SHARED_COPY.headlineLead}
            <br />
            <em className="font-light">{ABOUT_SHARED_COPY.headlineEm}</em>
          </h1>
          <p
            className="mx-auto mt-9 max-w-[560px] text-[19px] font-light leading-[1.7]"
            style={{ color: v.mute }}
          >
            <em style={{ fontFamily: display, color: v.bone }} className="text-2xl italic">
              {ABOUT_SHARED_COPY.lede}
            </em>
            <br />
            <br />
            {ABOUT_SHARED_COPY.opener}
          </p>
        </div>
        <figure className="mx-auto mt-16 max-w-[1084px] sm:mt-[72px]">
          <img
            src={`${CDN_BASE}/DSCF9542.JPG`}
            alt="The founding trip — a group of friends at golden hour"
            className="block h-[320px] w-full object-cover sm:h-[560px]"
            loading="eager"
          />
          <figcaption
            className="px-0.5 pt-3.5 font-mono text-xs tracking-[0.08em]"
            style={{ color: v.dim }}
          >
            &uarr; The trip where it clicked &mdash; friends-of-friends, nothing
            to prove
          </figcaption>
        </figure>
      </section>

      {/* CHAPTER I — the story */}
      <section className="px-6 py-24 sm:py-32">
        <div className="mx-auto mb-12 max-w-[640px]">
          <ChapterMark numeral="I" />
        </div>
        <div className="mx-auto max-w-[640px]">
          <p className="mb-7 text-lg leading-[1.85]" style={{ color: v.ivory }}>
            <span
              className="float-left pr-3 pt-2.5 text-[74px] leading-[0.75]"
              style={{ fontFamily: display, color: v.bone }}
            >
              O
            </span>
            ne of us had just come out of a 5-year relationship&mdash;thirty-five,
            newly single, surrounded by married friends with toddlers and brunch
            plans that wrapped by 2pm. The other was in the middle of his own
            reinvention&mdash;a new chapter of work, a new city on the horizon,
            and a long-simmering itch for community and creativity that his day
            job never had room for.
          </p>
          <p className="mb-7 text-lg leading-[1.85]" style={{ color: v.ivory }}>
            {ABOUT_SHARED_COPY.groupTrip}
          </p>
          <p className="text-lg leading-[1.85]" style={{ color: v.ivory }}>
            And something clicked. Not because it was &ldquo;exclusive&rdquo; or
            &ldquo;elevated&rdquo;&mdash;it worked because no one had anything to
            prove. Good humans, open to connection, willing to drop the LinkedIn
            mask and actually show up.
          </p>
        </div>
        <div className="mx-auto mt-20 max-w-[880px] text-center sm:mt-24">
          <p
            style={{ fontFamily: display, color: v.bone }}
            className="text-[30px] font-light italic leading-[1.35] sm:text-[46px]"
          >
            &ldquo;{ABOUT_SHARED_COPY.birthQuote}&rdquo;
          </p>
        </div>
      </section>

      {/* CHAPTER II — the journey so far */}
      <section className="px-6 pb-24 sm:pb-32">
        <div className="mb-16 sm:mb-20">
          <ChapterMark numeral="II" title="The journey so far" />
        </div>
        <div className="mx-auto flex max-w-[1084px] flex-col gap-20 sm:gap-[110px]">
          {ERAS.map((era, i) => (
            <div
              key={era.year}
              className="grid items-center gap-10 sm:grid-cols-2 sm:gap-[72px]"
            >
              <img
                src={era.image}
                alt={`${era.title} — rendezvous ${era.year}`}
                loading="lazy"
                className={`h-[280px] w-full object-cover sm:h-[440px] ${
                  i % 2 === 1 ? 'sm:order-2' : ''
                }`}
              />
              <div className={i % 2 === 1 ? 'sm:order-1' : ''}>
                <div
                  style={{ fontFamily: display, color: v.brassMute }}
                  className="mb-5 text-[56px] font-light leading-[1] sm:text-[80px]"
                >
                  {era.year}
                </div>
                <h3
                  style={{ fontFamily: display, color: v.bone }}
                  className="mb-4 text-[28px] font-normal sm:text-[32px]"
                >
                  {era.title}
                </h3>
                <p
                  className="mb-4 text-[17px] leading-[1.8]"
                  style={{ color: v.mute }}
                >
                  {era.body}
                </p>
                {era.aside ? (
                  <p className="text-[17px] italic" style={{ color: v.ivory }}>
                    {era.aside}
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CHAPTER III — what we believe */}
      <section
        className="px-6 py-24 sm:py-32"
        style={{
          backgroundColor: v.void,
          borderTop: `1px solid ${v.hair}`,
          borderBottom: `1px solid ${v.hair}`,
        }}
      >
        <div className="mb-14 sm:mb-[72px]">
          <ChapterMark numeral="III" title="What we believe" />
        </div>
        <div className="mx-auto max-w-[760px]">
          {BELIEFS.map((belief, i) => (
            <div
              key={belief.statement}
              className="flex flex-col gap-2 py-[34px] sm:flex-row sm:items-baseline sm:gap-8"
              style={{
                borderTop: `1px solid ${v.hair}`,
                borderBottom:
                  i === BELIEFS.length - 1 ? `1px solid ${v.hair}` : undefined,
              }}
            >
              <span
                style={{ fontFamily: display, color: v.bone }}
                className="flex-none text-[28px] font-light sm:min-w-[340px] sm:text-[34px]"
              >
                {belief.statement}
              </span>
              <span
                className="text-base leading-[1.6]"
                style={{ color: v.mute }}
              >
                {belief.detail}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* PROOF — not promises */}
      <section className="pt-24 sm:pt-32">
        <div className="mx-auto max-w-[760px] px-6 pb-14 text-center sm:pb-16">
          <Eyebrow>Proof, not promises</Eyebrow>
          <h2
            style={{ fontFamily: display, color: v.bone }}
            className="mt-6 text-[34px] font-light leading-[1.2] sm:text-[48px]"
          >
            These aren&rsquo;t stock photos.
            <br />
            They&rsquo;re last year.
          </h2>
        </div>
        <div className="grid auto-rows-[160px] grid-cols-2 gap-1 sm:auto-rows-[220px] sm:grid-cols-3">
          <figure className="relative row-span-2 h-full w-full overflow-hidden">
            <img
              src={carousel(3)}
              alt="Friends gathered around a candlelit dinner table at night"
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <figcaption
              className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-10 font-mono text-[11px] tracking-[0.08em]"
              style={{ color: v.bone }}
            >
              The long table, by candlelight
            </figcaption>
          </figure>
          <figure className="relative h-full w-full overflow-hidden">
            <img
              src={carousel(14)}
              alt="The group together at golden hour by the water"
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <figcaption
              className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-10 font-mono text-[11px] tracking-[0.08em]"
              style={{ color: v.bone }}
            >
              Golden hour, the whole crew
            </figcaption>
          </figure>
          <figure className="relative h-full w-full overflow-hidden">
            <img
              src={carousel(20)}
              alt="Friends around a lunch table under a vine-covered pergola"
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <figcaption
              className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-10 font-mono text-[11px] tracking-[0.08em]"
              style={{ color: v.bone }}
            >
              Lunch ran long
            </figcaption>
          </figure>
          <figure className="relative col-span-2 sm:col-span-2 h-full w-full overflow-hidden">
            <img
              src={carousel(27)}
              alt="The full group gathered in front of an Indian palace"
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <figcaption
              className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-10 font-mono text-[11px] tracking-[0.08em]"
              style={{ color: v.bone }}
            >
              India, the whole group
            </figcaption>
          </figure>
        </div>
      </section>

      {/* CLOSE */}
      <section className="relative overflow-hidden">
        <img
          src={carousel(12)}
          alt=""
          aria-hidden
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{ backgroundColor: 'rgba(0,0,0,0.72)' }}
        />
        <div className="relative px-6 py-28 text-center sm:py-[150px]">
          <p
            style={{ fontFamily: display, color: v.bone }}
            className="mx-auto mb-5 max-w-[820px] text-[36px] font-light leading-[1.25] sm:text-[52px]"
          >
            Curious, warm, and allergic to bullshit?
          </p>
          <p
            className="mb-12 text-[19px] font-light sm:mb-[52px]"
            style={{ color: v.mute }}
          >
            You&rsquo;re in the right place.
          </p>
          {!user && (
            <button
              type="button"
              onClick={() => openAuthModal('sign-up')}
              className="inline-block cursor-pointer px-10 py-[18px] text-[11px] font-semibold uppercase tracking-[2px]"
              style={{ backgroundColor: v.brass, color: v.void }}
            >
              Join rendezvous
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
